// VH-67: Renders one column per ISO week from findings.weeks.
// CSS grid auto-fits columns; on mobile the columns stack vertically.
import { useTranslation } from 'react-i18next'
import {
  FireIcon,
  BoltIcon,
  HeartIcon,
  ScaleIcon,
  MoonIcon,
} from '@heroicons/react/24/outline'
import { parseIsoWeekKey } from './isoWeek'

const sectionStyles = {
  nutrition: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600' },
  activity: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600' },
  exercise: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600' },
  body: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600' },
  wellness: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: 'text-indigo-600' },
}

function fmtNumber(value, opts = {}) {
  if (value == null || Number.isNaN(value)) return null
  if (opts.round) return Math.round(value).toLocaleString()
  if (typeof opts.fractionDigits === 'number') {
    return Number(value).toFixed(opts.fractionDigits)
  }
  return Number(value).toLocaleString()
}

function fmtChange(value, fractionDigits = 1) {
  if (value == null || Number.isNaN(value)) return null
  const sign = value > 0 ? '+' : ''
  return `${sign}${Number(value).toFixed(fractionDigits)}`
}

function MetricRow({ label, value, unit }) {
  return (
    <div className="flex justify-between items-baseline py-1 border-b border-white/40 last:border-b-0">
      <span className="text-xs text-gray-600">{label}</span>
      <span className="text-sm font-semibold text-gray-900">
        {value == null ? '--' : value}
        {value != null && unit && <span className="text-xs font-normal text-gray-500 ml-1">{unit}</span>}
      </span>
    </div>
  )
}

function NutritionCard({ data, t }) {
  const s = sectionStyles.nutrition
  const hasData = data && data.days_on_target != null
  return (
    <SectionCard
      icon={<FireIcon className={`h-4 w-4 ${s.icon}`} />}
      title={t('reports.weekly.nutrition')}
      style={s}
    >
      {hasData ? (
        <>
          <MetricRow
            label={t('reports.weekly.daysOnTarget')}
            value={fmtNumber(data.days_on_target)}
            unit="/7"
          />
          <MetricRow
            label={t('reports.weekly.avgCalories')}
            value={fmtNumber(data.avg_calories, { round: true })}
            unit={t('reports.weekly.kcal')}
          />
          <MetricRow
            label={t('reports.weekly.avgProtein')}
            value={fmtNumber(data.avg_protein, { round: true })}
            unit={t('reports.weekly.grams')}
          />
          <MetricRow
            label={t('reports.weekly.avgCarbs')}
            value={fmtNumber(data.avg_carbs, { round: true })}
            unit={t('reports.weekly.grams')}
          />
          <MetricRow
            label={t('reports.weekly.avgFat')}
            value={fmtNumber(data.avg_fat, { round: true })}
            unit={t('reports.weekly.grams')}
          />
        </>
      ) : (
        <p className="text-xs text-gray-500 text-center py-2">{t('reports.weekly.noData')}</p>
      )}
    </SectionCard>
  )
}

function ActivityCard({ data, t }) {
  const s = sectionStyles.activity
  const hasData = data && (data.avg_steps != null || data.avg_sleep_hours != null)
  return (
    <SectionCard
      icon={<HeartIcon className={`h-4 w-4 ${s.icon}`} />}
      title={t('reports.weekly.activity')}
      style={s}
    >
      {hasData ? (
        <>
          <MetricRow
            label={t('reports.weekly.avgSteps')}
            value={fmtNumber(data.avg_steps, { round: true })}
            unit={t('reports.weekly.steps')}
          />
          <MetricRow
            label={t('reports.weekly.avgSleepHours')}
            value={fmtNumber(data.avg_sleep_hours, { fractionDigits: 1 })}
            unit={t('reports.weekly.hours')}
          />
          <MetricRow
            label={t('reports.weekly.avgRestingHR')}
            value={fmtNumber(data.avg_resting_hr, { round: true })}
            unit={t('reports.weekly.bpm')}
          />
          <MetricRow
            label={t('reports.weekly.intensityMinutes')}
            value={fmtNumber(data.total_intensity_minutes, { round: true })}
            unit="min"
          />
        </>
      ) : (
        <p className="text-xs text-gray-500 text-center py-2">{t('reports.weekly.noData')}</p>
      )}
    </SectionCard>
  )
}

function ExerciseCard({ data, t }) {
  const s = sectionStyles.exercise
  const hasData = data && data.sessions_completed != null
  return (
    <SectionCard
      icon={<BoltIcon className={`h-4 w-4 ${s.icon}`} />}
      title={t('reports.weekly.exercise')}
      style={s}
    >
      {hasData ? (
        <>
          <MetricRow
            label={t('reports.weekly.sessionsCompleted')}
            value={fmtNumber(data.sessions_completed)}
          />
          <MetricRow
            label={t('reports.weekly.exercisesPerformed')}
            value={fmtNumber(data.exercises_performed)}
          />
          <MetricRow
            label={t('reports.weekly.newPRs')}
            value={fmtNumber(data.new_prs)}
          />
          <MetricRow
            label={t('reports.weekly.weekScore')}
            value={data.week_score != null ? `${Math.round(data.week_score)}` : null}
            unit="/100"
          />
        </>
      ) : (
        <p className="text-xs text-gray-500 text-center py-2">{t('reports.weekly.noData')}</p>
      )}
    </SectionCard>
  )
}

function BodyCard({ data, t }) {
  const s = sectionStyles.body
  const hasData = data && (data.weight_delta_kg != null || data.bodyfat_delta_pct != null)
  return (
    <SectionCard
      icon={<ScaleIcon className={`h-4 w-4 ${s.icon}`} />}
      title={t('reports.weekly.body')}
      style={s}
    >
      {hasData ? (
        <>
          <MetricRow
            label={t('reports.weekly.weightChange')}
            value={fmtChange(data.weight_delta_kg)}
            unit={t('reports.weekly.kg')}
          />
          <MetricRow
            label={t('reports.weekly.bodyFatChange')}
            value={fmtChange(data.bodyfat_delta_pct)}
            unit={t('reports.weekly.percent')}
          />
        </>
      ) : (
        <p className="text-xs text-gray-500 text-center py-2">{t('reports.weekly.noMeasurements')}</p>
      )}
    </SectionCard>
  )
}

function WellnessCard({ data, t }) {
  const s = sectionStyles.wellness
  const sleepPhases = data?.sleep_phase_avgs || {}
  const hasData = data && (
    sleepPhases.deep_minutes != null ||
    sleepPhases.light_minutes != null ||
    sleepPhases.rem_minutes != null ||
    data.stress_avg != null
  )
  // Avg total sleep = sum of the three productive phases (awake excluded)
  const totalSleepMin = (
    (sleepPhases.deep_minutes ?? 0) +
    (sleepPhases.light_minutes ?? 0) +
    (sleepPhases.rem_minutes ?? 0)
  )
  const hasAnyPhase = (
    sleepPhases.deep_minutes != null ||
    sleepPhases.light_minutes != null ||
    sleepPhases.rem_minutes != null
  )
  const totalSleepHours = hasAnyPhase ? totalSleepMin / 60 : null
  return (
    <SectionCard
      icon={<MoonIcon className={`h-4 w-4 ${s.icon}`} />}
      title={t('reports.weekly.wellness')}
      style={s}
    >
      {hasData ? (
        <>
          {totalSleepHours != null && (
            <MetricRow
              label={t('reports.weekly.totalSleep')}
              value={fmtNumber(totalSleepHours, { fractionDigits: 1 })}
              unit={t('reports.weekly.hours')}
            />
          )}
          {sleepPhases.deep_minutes != null && (
            <MetricRow
              label={t('reports.weekly.deepSleep')}
              value={fmtNumber(sleepPhases.deep_minutes, { round: true })}
              unit="min"
            />
          )}
          {sleepPhases.light_minutes != null && (
            <MetricRow
              label={t('reports.weekly.lightSleep')}
              value={fmtNumber(sleepPhases.light_minutes, { round: true })}
              unit="min"
            />
          )}
          {sleepPhases.rem_minutes != null && (
            <MetricRow
              label={t('reports.weekly.remSleep')}
              value={fmtNumber(sleepPhases.rem_minutes, { round: true })}
              unit="min"
            />
          )}
          <MetricRow
            label={t('reports.weekly.stressAvg')}
            value={fmtNumber(data.stress_avg, { fractionDigits: 1 })}
          />
        </>
      ) : (
        <p className="text-xs text-gray-500 text-center py-2">{t('reports.weekly.noData')}</p>
      )}
    </SectionCard>
  )
}

function SectionCard({ icon, title, style, children }) {
  return (
    <div className={`rounded-lg border ${style.border} ${style.bg} p-3`}>
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
      </div>
      <div>{children}</div>
    </div>
  )
}

export default function PerWeekColumns({ weeks }) {
  const { t } = useTranslation()

  if (!weeks || Object.keys(weeks).length === 0) {
    return (
      <div className="text-center py-6 text-sm text-gray-500">
        {t('reports.range.noData')}
      </div>
    )
  }

  // Order chronologically by ISO week key (YYYY-Www sorts naturally as strings).
  const orderedKeys = Object.keys(weeks).sort()

  return (
    <div className="grid gap-4 md:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
      {orderedKeys.map((key) => {
        const parsed = parseIsoWeekKey(key)
        const week = weeks[key] || {}
        const headerLabel = parsed
          ? t('reports.range.label', { week: parsed.week, year: parsed.year })
          : key
        return (
          <div key={key} className="border border-gray-200 rounded-lg p-3 bg-surface-container">
            <div className="text-center pb-2 mb-3 border-b">
              <h3 className="font-semibold text-gray-900">{headerLabel}</h3>
            </div>
            <div className="space-y-3">
              <NutritionCard data={week.nutrition} t={t} />
              <ActivityCard data={week.activity} t={t} />
              <ExerciseCard data={week.exercise} t={t} />
              <BodyCard data={week.body} t={t} />
              <WellnessCard data={week.wellness} t={t} />
            </div>
          </div>
        )
      })}
    </div>
  )
}
